import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { guardarUbicacionManual } from '../components/mapa/UbicacionEnMapa';
import { toast } from 'react-hot-toast';
import { getTodayISO, formatDateISO, estadoGarantia } from '../utils/dateUtils';
import { toTitleCase } from '../utils/titleCase';
import { subirFotosTrabajo } from '../utils/subirFoto';
import { rotulosTrasSubir } from '../utils/rotulosFotos';
import { claveBorrador, guardarBorrador, hayBorrador, leerBorrador, borrarBorrador } from '../utils/borradores';

// Borrador por usuario (20-oct-2026): en un celu compartido o al cambiar de usuario no se mezclan.
// Las fotos sin subir van a IndexedDB (utils/borradores), así no se pierden ni llenan localStorage.
const draftKey = () => claveBorrador('servicio_borrador');
// Hay algo que valga la pena recuperar: un equipo, un trabajo escrito o un cliente nuevo a medio cargar
const borradorConContenido = (d) => (d?.ticketItems?.length > 0) || !!d?.itemActual?.trabajo?.trim?.()
  || !!d?.itemActual?.equipoSerial || !!d?.nombreLibre?.trim?.() || !!d?.dirLibre?.calle?.trim?.();
// Nombre para el aviso "Tenés un trabajo sin terminar para …"
export const nombreDelBorrador = () => {
  try {
    const d = hayBorrador(draftKey()) || {};
    return d.nombreLibre?.trim() || d.clienteNombre || '';
  } catch { return ''; }
};

// Montos de un ítem tal como los guarda el backend: costo = total del ítem
// (mano de obra + repuestos), costoExtra = mano de obra. Se usa igual para
// guardar y para pedir la vista previa del total, así nunca difieren.
export const montosItem = it => ({
  costo:      parseFloat(it.totalCalculado) || parseFloat(it.costoExtra) || 0,
  costoExtra: parseFloat(it.costoExtra) || 0,
});

export function useServicioForm(servicioParaEditar = null, clienteInicialId = null, presupuestoOrigen = null, ordenOrigen = null) {
  const [db, setDb] = useState({ clientes: [], sedes: [], equipos: [], repuestos: [] });
  const [configGlobal, setConfigGlobal] = useState(null);

  // Borrador: true si existe un draft guardado al montar el formulario (solo en creación)
  const [borradorDisponible, setBorradorDisponible] = useState(() => {
    if (servicioParaEditar) return false;
    try {
      // El borrador viejo (antes del 20-oct, sin usuario) pasa al de este usuario
      const viejo = localStorage.getItem('servicio_borrador');
      if (viejo && !localStorage.getItem(draftKey())) localStorage.setItem(draftKey(), viejo);
      if (viejo) localStorage.removeItem('servicio_borrador');
      const raw = localStorage.getItem(draftKey());
      if (!raw) return false;
      return borradorConContenido(JSON.parse(raw));
    } catch { return false; }
  });
  // Evita que el effect de auto-save borre el draft antes de que el usuario lo recupere
  const autoSaveReady = useRef(false);
  const [clienteId, setClienteId] = useState(clienteInicialId ? String(clienteInicialId) : null);
  const [esPresupuesto, setEsPresupuesto] = useState(true);
  const [ticketItems, setTicketItems] = useState([]);
  const [idEdicion, setIdEdicion] = useState(null);
  const [estaBloqueado, setEstaBloqueado] = useState(false);
  const [historialEquipo, setHistorialEquipo] = useState(null);
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState(0);
  // Sobre qué se aplica el %: TOTAL | MANO_DE_OBRA | REPUESTOS (9-oct-2026)
  const [descuentoAlcance, setDescuentoAlcance] = useState('TOTAL');
  // Total calculado por el backend (única fuente): { manoDeObra, repuestos, subtotal, descuentoMonto, total, ... }
  const [totales, setTotales] = useState(null);

  const [modalClienteAbierto, setModalClienteAbierto] = useState(false);
  const [nombreClientePrellenado, setNombreClientePrellenado] = useState('');
  const [modalSedeAbierto, setModalSedeAbierto] = useState(false);
  const [nombreSedePrellenado, setNombreSedePrellenado] = useState('');

  const [itemActual, setItemActual] = useState({
    sedeId: '', sedeNombre: '', equipoSerial: '',
    trabajo: '', costoExtra: '', repuestosUsados: [],
    fotoAntes: null, fotoDespues: null,
  });

  const [editingIdx, setEditingIdx] = useState(null); // índice original al editar un item
  const [repuestoElegido, setRepuestoElegido] = useState(null);
  const [tecnicoSeleccionado, setTecnicoSeleccionado] = useState(null); // { id, nombre } — solo admin

  // Estado del PasoCliente — persistido acá para que no se pierda al cambiar de paso
  const [modoCliente, setModoCliente] = useState('registrado');
  const [nombreLibre, setNombreLibre] = useState('');
  const [telefonoLibre, setTelefonoLibre] = useState('');
  const [dirLibre, setDirLibre] = useState({ calle: '', numero: '', piso: '', depto: '', localidad: '' });
  // Punto del mapa en vivo del cliente nuevo (si lo corrigieron a mano, se guarda con la dirección)
  const [puntoLibre, setPuntoLibre] = useState(null);
  const [fechaVisita, setFechaVisita] = useState(''); // fecha estimada de visita para auto-despacho
  // Franja que se tocó en la agenda al elegir técnico (26-oct-2026: se perdía y la visita quedaba "sin horario")
  const [horaVisita, setHoraVisita] = useState('');
  const LEYENDA_DEFAULT = 'Garantía: 90 días sobre mano de obra · Repuestos según fabricante';
  const [leyenda, setLeyenda] = useState(LEYENDA_DEFAULT);
  const [fotosTrabajo, setFotosTrabajo] = useState([]);   // hasta 4 fotos del trabajo (14-oct-2026)
  const [fechaServicio, setFechaServicio] = useState(getTodayISO());
  const [duracionMinutos, setDuracionMinutos] = useState(null);
  const [fechaTentativa, setFechaTentativa] = useState(false); // true = todavia no se coordino dia/hora exacto con el cliente
  const [ventanasDisponibles, setVentanasDisponibles] = useState([]); // [{ dia, franja }] habilitados por el admin
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [c, s, e, r, cfg] = await Promise.all([
          api.get('/clientes?page=0&size=500'),
          api.get('/sedes?page=0&size=500'),
          api.get('/equipos?page=0&size=500'),
          api.get('/repuestos?page=0&size=500'),
          api.get('/configuracion').catch(() => ({ data: { manoDeObraBase: 72600, porcentajeImpuestos: 30, porcentajeIVA: 21 } })),
        ]);
        setDb({
          clientes:  c.data.content || c.data,
          sedes:     s.data.content || s.data,
          equipos:   e.data.content || e.data,
          repuestos: r.data.content || r.data
        });
        // Guardar config para calcular precioCliente default
        const conf = cfg.data;
        setConfigGlobal(conf);
        // Pre-llenar mano de obra con precio con IVA (moBase ya incluye IVA)
        if (!servicioParaEditar && !presupuestoOrigen && !ordenOrigen) {
          const moBase = Number(conf.manoDeObraBase) || 72600;
          setItemActual(prev => ({ ...prev, costoExtra: moBase }));
        }

        // Enriquece repuestos del backend con costo/porcentajeGanancia desde el catálogo local
        const repuestosDB = r.data.content || r.data;
        const enriquecerRepuestos = (repuestos) =>
          (repuestos || []).map(rep => {
            if (rep.costo > 0 || rep.porcentajeGanancia > 0) return rep;
            const catalogo = repuestosDB.find(x => x.id === rep.id);
            if (!catalogo) return rep;
            return {
              ...rep,
              costo:              parseFloat(catalogo.costo) || 0,
              porcentajeGanancia: parseFloat(catalogo.porcentajeGanancia) || 0,
            };
          });

        if (ordenOrigen) {
          setEsPresupuesto(false);
          if (ordenOrigen.descripcion) setLeyenda(ordenOrigen.descripcion);
          // Si la orden tiene presupuesto vinculado, pre-llenarlo como presupuestoOrigen
          if (ordenOrigen.presupuestoId) {
            try {
              const { data: p } = await api.get(`/servicios/${ordenOrigen.presupuestoId}`);
              setClienteId(p.clienteId?.toString() || null);
              setDescuentoPorcentaje(p.descuentoPorcentaje || 0);
              setDescuentoAlcance(p.descuentoAlcance || 'TOTAL');
              if (p.observaciones) setLeyenda(p.observaciones);
              setItemActual(prev => ({ ...prev, sedeId: p.sedeId, sedeNombre: p.sedeNombre }));
              setTicketItems(
                (p.items || []).map(it => ({
                  sedeId:            p.sedeId,
                  sedeNombre:        p.sedeNombre,
                  equipoSerial:      it.equipoSerial || 'MOSTRADOR',
                  modeloEquipo:      it.equipoModelo || null,
                  ubicacionEquipo:   it.equipoUbicacion || null,
                  trabajo:           it.trabajoRealizado || '',
                  costoExtra:        Math.max(0, Number(it.costoExtra) || 0),
                  totalCalculado:    Math.max(0, Number(it.costo)),
                  totalSinDescuento: Math.max(0, Number(it.costo)),
                  repuestosUsados:   enriquecerRepuestos(it.repuestosUsados),
                  resumenTexto:      it.trabajoRealizado || '',
                  // Las fotos del presupuesto (las que mandó el cliente) pasan al trabajo con su rótulo (18-oct-2026)
                  fotoAntes:         it.fotoAntes || null,
                  fotoDespues:       it.fotoDespues || null,
                  fotosExtra:        it.fotosExtra || [],
                  fotosRotulos:      it.fotosRotulos || null,
                }))
              );
            } catch { toast.error('No se pudo cargar el presupuesto vinculado. Completá los datos manualmente.'); }
          }
        } else if (presupuestoOrigen) {
          // Pre-llenar desde presupuesto: el técnico confirma/modifica lo realmente hecho
          setEsPresupuesto(false); // El servicio resultante será REALIZADO
          setClienteId(presupuestoOrigen.clienteId?.toString() || null);
          setDescuentoPorcentaje(presupuestoOrigen.descuentoPorcentaje || 0);
          setDescuentoAlcance(presupuestoOrigen.descuentoAlcance || 'TOTAL');
          if (presupuestoOrigen.observaciones) setLeyenda(presupuestoOrigen.observaciones);
          setItemActual(prev => ({ ...prev, sedeId: presupuestoOrigen.sedeId, sedeNombre: presupuestoOrigen.sedeNombre }));
          setTicketItems(
            (presupuestoOrigen.items || []).map(it => ({
              sedeId:            presupuestoOrigen.sedeId,
              sedeNombre:        presupuestoOrigen.sedeNombre,
              equipoSerial:      it.equipoSerial || 'MOSTRADOR',
              modeloEquipo:      it.equipoModelo || null,
              ubicacionEquipo:   it.equipoUbicacion || null,
              trabajo:           it.trabajoRealizado || '',
              costoExtra:        Math.max(0, Number(it.costoExtra) || 0),
              totalCalculado:    Math.max(0, Number(it.costo)),
              totalSinDescuento: Math.max(0, Number(it.costo)),
              repuestosUsados:   enriquecerRepuestos(it.repuestosUsados),
              resumenTexto:      it.trabajoRealizado || '',
              // Las fotos del presupuesto pasan al trabajo con su rótulo (18-oct-2026)
              fotoAntes:         it.fotoAntes || null,
              fotoDespues:       it.fotoDespues || null,
              fotosExtra:        it.fotosExtra || [],
              fotosRotulos:      it.fotosRotulos || null,
            }))
          );
        } else if (servicioParaEditar) {
          // Bug 5-oct-2026: bloqueaba todo lo que no fuera PRESUPUESTO, incluido un trabajo
          // asignado (EN_PROGRESO): mostraba "cobrado — solo lectura" y Guardar no hacía nada.
          // 15-oct-2026: hecho pero sin facturar (COMPLETADO / PENDIENTE_FACTURACION) se puede corregir
          // (N/S, qué se hizo, repuestos) sin que cambie de estado; facturado o cobrado, no.
          setEstaBloqueado(['FACTURADO', 'COBRADO', 'REALIZADO', 'ARCHIVADO', 'CANCELADO'].includes(servicioParaEditar.estado));
          setIdEdicion(servicioParaEditar.id);
          setEsPresupuesto(servicioParaEditar.servicioTipo === 'TECNICA');
          setDescuentoPorcentaje(servicioParaEditar.descuentoPorcentaje || 0);
          setDescuentoAlcance(servicioParaEditar.descuentoAlcance || 'TOTAL');
          setTicketItems(
            servicioParaEditar.items.map(it => ({
              sedeId:            servicioParaEditar.sedeId,
              equipoSerial:      it.equipoSerial,
              modeloEquipo:      it.equipoModelo   || null,
              ubicacionEquipo:   it.equipoUbicacion || null,
              trabajo:           it.trabajoRealizado,
              costoExtra:        Math.max(0, Number(it.costoExtra) || 0),
              totalCalculado:    Math.max(0, Number(it.costo)),
              totalSinDescuento: Math.max(0, Number(it.costo)),
              repuestosUsados:   enriquecerRepuestos(it.repuestosUsados),
              resumenTexto:      it.trabajoRealizado,
              fotoAntes:         it.fotoAntes   || null,
              fotoDespues:       it.fotoDespues || null,
              fotosExtra:        it.fotosExtra || [],
              fotosRotulos:      it.fotosRotulos || null,
            }))
          );
          setItemActual(prev => ({ ...prev, sedeId: servicioParaEditar.sedeId }));
          if (servicioParaEditar.observaciones) setLeyenda(servicioParaEditar.observaciones);
          setFotosTrabajo(servicioParaEditar.fotosTrabajo || []);
          // Recuperar técnico y fecha del servicio original
          if (servicioParaEditar.usuarioId) {
            setTecnicoSeleccionado({ id: servicioParaEditar.usuarioId, nombre: servicioParaEditar.usuarioNombre || '' });
          }
          if (servicioParaEditar.fecha) {
            // fecha puede venir como "dd/MM/yyyy" o "yyyy-MM-dd"
            const f = servicioParaEditar.fecha;
            let fechaISO = f;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(f)) {
              const [d, m, a] = f.split('/');
              fechaISO = `${a}-${m}-${d}`;
            }
            setFechaServicio(fechaISO);
            // Pre-llenar fecha de visita para que no la pida de nuevo
            if (servicioParaEditar.usuarioId) setFechaVisita(fechaISO);
          }
          if (servicioParaEditar.duracionMinutos) setDuracionMinutos(servicioParaEditar.duracionMinutos);
          if (servicioParaEditar.fechaTentativa) setFechaTentativa(true);
          if (servicioParaEditar.ventanasDisponibles) {
            try { setVentanasDisponibles(JSON.parse(servicioParaEditar.ventanasDisponibles)); } catch { /* ignora JSON invalido */ }
          }
          // clienteId viene del backend ahora
          if (servicioParaEditar.clienteId) {
            setClienteId(servicioParaEditar.clienteId.toString());
          }
        }
      } catch {
        toast.error('Error de conexión');
      }
    };
    cargar();
  }, [servicioParaEditar]);

  // Auto-guardar borrador en localStorage cada vez que cambia el formulario.
  // Se salta el primer render (mount) para no borrar un draft existente con estado vacío.
  useEffect(() => {
    if (servicioParaEditar) return;
    if (borradorDisponible) return; // No pisar el draft mientras el banner está visible
    if (!autoSaveReady.current) { autoSaveReady.current = true; return; }

    // 20-oct-2026: el cliente nuevo a medio cargar también cuenta (antes, si salías de la app a
    // sacar una foto, volvía el trabajo pero se perdía el cliente que estabas creando).
    const hayContenido = ticketItems.length > 0
      || itemActual.trabajo?.trim()
      || itemActual.equipoSerial?.trim()
      || itemActual.repuestosUsados?.length > 0
      || (modoCliente === 'nuevo' && (nombreLibre.trim() || dirLibre.calle.trim()));

    if (!hayContenido) { borrarBorrador(draftKey()); return undefined; }

    // Espera a que se deje de escribir un momento (no guarda en cada tecla)
    const t = setTimeout(() => {
      guardarBorrador(draftKey(), {
        ticketItems, itemActual, clienteId,
        fechaServicio, descuentoPorcentaje, descuentoAlcance, leyenda,
        duracionMinutos, tecnicoSeleccionado,
        modoCliente, nombreLibre, telefonoLibre, dirLibre, puntoLibre, fechaVisita, fotosTrabajo,
        clienteNombre: db.clientes?.find(c => c.id?.toString() === clienteId)?.nombre || '',
        ts: Date.now(),
      });
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketItems, itemActual, clienteId, fechaServicio, descuentoPorcentaje, descuentoAlcance, leyenda, borradorDisponible,
      modoCliente, nombreLibre, telefonoLibre, dirLibre, puntoLibre, fechaVisita, fotosTrabajo, duracionMinutos, tecnicoSeleccionado]);

  const recuperarBorrador = async () => {
    try {
      const d = (await leerBorrador(draftKey())) || {};
      if (d.ticketItems)             setTicketItems(d.ticketItems);
      if (d.itemActual)              setItemActual(d.itemActual);
      if (d.clienteId)               setClienteId(d.clienteId);
      if (d.fechaServicio)           setFechaServicio(d.fechaServicio);
      if (d.descuentoPorcentaje !== undefined) setDescuentoPorcentaje(d.descuentoPorcentaje);
      if (d.descuentoAlcance)        setDescuentoAlcance(d.descuentoAlcance);
      if (d.leyenda)                 setLeyenda(d.leyenda);
      if (d.duracionMinutos)         setDuracionMinutos(d.duracionMinutos);
      if (d.tecnicoSeleccionado)     setTecnicoSeleccionado(d.tecnicoSeleccionado);
      if (d.modoCliente)             setModoCliente(d.modoCliente);
      if (d.nombreLibre)             setNombreLibre(d.nombreLibre);
      if (d.telefonoLibre)           setTelefonoLibre(d.telefonoLibre);
      if (d.dirLibre)                setDirLibre(prev => ({ ...prev, ...d.dirLibre }));
      if (d.puntoLibre)              setPuntoLibre(d.puntoLibre);
      if (d.fechaVisita)             setFechaVisita(d.fechaVisita);
      if (Array.isArray(d.fotosTrabajo)) setFotosTrabajo(d.fotosTrabajo.filter(Boolean));
      setBorradorDisponible(false);
      toast.success('Seguís donde lo dejaste');
    } catch { toast.error('Error al recuperar el borrador'); }
  };

  const descartarBorrador = () => {
    borrarBorrador(draftKey());
    setBorradorDisponible(false);
  };

  const onClienteSeleccionado = (id) => {
    setClienteId(id);
    const sedesDelCliente = db.sedes.filter(s => s.cliente?.id?.toString() === id);
    if (sedesDelCliente.length === 1) {
      const sede = sedesDelCliente[0];
      setItemActual(prev => ({ ...prev, sedeId: sede.id, sedeNombre: sede.nombreSede }));
    } else {
      setItemActual(prev => ({ ...prev, sedeId: '', sedeNombre: '' }));
    }
  };

  // Si el cliente no tiene sedes, crea "Principal" con su dirección y la agrega al db local
  const crearSedePrincipalSiNoTiene = async (clienteIdStr) => {
    if (!clienteIdStr) return null;
    const sedesActuales = db.sedes?.filter(s => s.cliente?.id?.toString() === clienteIdStr) || [];
    if (sedesActuales.length > 0) return sedesActuales[0];
    const clienteObj = db.clientes?.find(c => c.id?.toString() === clienteIdStr);
    if (!clienteObj) return null;
    try {
      const { data: nuevaSede } = await api.post('/sedes', {
        clienteId:  parseInt(clienteIdStr),
        nombreSede: 'Principal',
        calle:      clienteObj.calle     || null,
        numero:     clienteObj.numero    || null,
        piso:       clienteObj.piso      || null,
        depto:      clienteObj.depto     || null,
        localidad:  clienteObj.localidad || null,
        provincia:  clienteObj.provincia || null,
        direccion:  clienteObj.direccion || null,
      });
      setDb(prev => ({ ...prev, sedes: [...(prev.sedes || []), nuevaSede] }));
      return nuevaSede;
    } catch { return null; }
  };

  const calcularGananciaRepuesto = (repuesto, cantidad) => {
    const precioVenta = parseFloat(repuesto.precio) || 0;
    const costo       = parseFloat(repuesto.costo)  || 0;
    const qty         = parseFloat(cantidad)         || 1;
    const subtotal    = precioVenta * qty;

    if (costo > 0) {
      const costoTotal = costo * qty;
      const ganancia   = subtotal - costoTotal;
      const margen     = subtotal > 0 ? ((ganancia / subtotal) * 100).toFixed(1) : 0;
      return { subtotal, costoTotal, ganancia, margen };
    }

    const pct = parseFloat(repuesto.porcentajeGanancia) || 0;
    if (pct > 0) {
      const costoImplicito = subtotal / (1 + pct / 100);
      const ganancia = subtotal - costoImplicito;
      return { subtotal, costoTotal: costoImplicito, ganancia, margen: pct.toFixed(1) };
    }

    return { subtotal, costoTotal: 0, ganancia: 0, margen: 0 };
  };

  // Vista previa del total: se pide al backend cada vez que cambian ítems, % o alcance.
  // Pequeña espera para no pedir en cada tecla; solo vale la última respuesta.
  const pedidoTotales = useRef(0);
  const pedirTotales = async () => {
    const n = ++pedidoTotales.current;
    const { data } = await api.post('/servicios/calcular-totales', {
      descuentoPorcentaje: Number(descuentoPorcentaje) || 0,
      descuentoAlcance,
      items: ticketItems.map(montosItem),
    });
    if (n === pedidoTotales.current) setTotales(data);
    return data;
  };
  useEffect(() => {
    setTotales(null);
    const t = setTimeout(() => { pedirTotales().catch(() => {}); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketItems, descuentoPorcentaje, descuentoAlcance]);
  // Para el PDF: usa el último total, o lo pide en el momento si todavía no llegó
  const obtenerTotales = async () => totales || pedirTotales();

  // El total con descuento lo calcula el backend (POST /servicios/calcular-totales,
  // mismo cálculo que usa al guardar). Acá solo se suma el costo de los repuestos,
  // que es privado y sirve para la rentabilidad. Mientras llega la respuesta,
  // `calculando` es true y los montos quedan en null (la UI muestra "…").
  const calcularResumenGanancia = () => {
    let totalCosto = 0;
    ticketItems.forEach(item => {
      item.repuestosUsados.forEach(r => { totalCosto += calcularGananciaRepuesto(r, r.cantidad).costoTotal; });
    });
    const t = totales;
    const totalConDescuento = t ? Number(t.total) : null;
    const gananciaBruta     = t ? totalConDescuento - totalCosto : null;
    const margenFinal       = t && totalConDescuento > 0
      ? ((gananciaBruta / totalConDescuento) * 100).toFixed(1) : 0;
    return {
      totales: t, calculando: !t,
      totalVenta: t ? Number(t.subtotal) : null,
      descuento:  t ? Number(t.descuentoMonto) : null,
      totalConDescuento, totalCosto, gananciaBruta, margenFinal,
    };
  };

  // ¿Existe este N/S en el inventario pero en una sede de otro cliente?
  const serialDeOtroCliente = serial => {
    const eq = db.equipos?.find(e => e.numeroSerie?.toUpperCase() === serial?.toUpperCase());
    if (!eq || !clienteId) return false;
    const sede = db.sedes?.find(sd => sd.id === eq.sedeId);
    return !!sede && sede.cliente?.id?.toString() !== clienteId;
  };

  const consultarAntecedentes = async serial => {
    if (!serial || serial === 'MOSTRADOR') { setHistorialEquipo(null); return true; }
    try {
      // El endpoint /servicios no tiene filtro "equipoSerial" — el filtro real es
      // "busqueda" (busca por N/S, cliente, sede, etc. via LIKE), y devuelve un
      // Page (con .content), no un array directo. Filtramos por coincidencia exacta
      // de serie porque "busqueda" matchea por substring.
      const res = await api.get(`/servicios?busqueda=${encodeURIComponent(serial)}&sort=fechaServicio,desc&size=50`);
      const lista = res.data?.content || res.data || [];
      const conEseSerial = lista.filter(s => s.items?.some(i => i.equipoSerial === serial));
      // Solo historial de ESTE cliente: el de otro cliente no se muestra (no tiene
      // sentido y expone datos ajenos). Si el N/S es de otro, se avisa y listo.
      const coincidencias = conEseSerial.filter(s => clienteId && s.clienteId?.toString() === clienteId);
      if (coincidencias.length === 0 && (conEseSerial.length > 0 || serialDeOtroCliente(serial))) {
        setHistorialEquipo(null);
        toast.error(`El N/S ${serial} ya está registrado en otro cliente. Usá otro o tocá "Generar".`, { duration: 6000 });
        return false;
      }
      if (coincidencias.length > 0) {
        const ultimo = coincidencias[0];
        // La garantía sale del último trabajo HECHO (no de un presupuesto). Si ese
        // ítem no tiene la fecha cargada (datos viejos), se estima con la regla del
        // backend: 3 meses desde la fecha del servicio.
        const TERMINADOS = ['COMPLETADO', 'PENDIENTE_FACTURACION', 'FACTURADO', 'COBRADO', 'REALIZADO'];
        const ultimoHecho = coincidencias.find(c => TERMINADOS.includes(c.estado)) || null;
        const itemGarantia = ultimoHecho?.items?.find(i => i.equipoSerial === serial && i.garantiaHasta);
        let garantiaHasta = itemGarantia?.garantiaHasta || null;
        let estimada = false;
        if (!garantiaHasta && ultimoHecho?.fecha) {
          const f = new Date(String(ultimoHecho.fecha).slice(0, 10) + 'T00:00:00');
          f.setMonth(f.getMonth() + 3);
          garantiaHasta = formatDateISO(f);
          estimada = true;
        }
        const garantiaInfo = garantiaHasta ? { ...estadoGarantia(garantiaHasta), estimada } : null;
        const diasDesde = ultimo.fecha
          ? Math.max(0, Math.round((new Date(getTodayISO() + 'T00:00:00') - new Date(String(ultimo.fecha).slice(0, 10) + 'T00:00:00')) / 86400000))
          : null;
        setHistorialEquipo({ ...ultimo, garantiaInfo, diasDesde, esPresupuesto: !TERMINADOS.includes(ultimo.estado) });
        if (garantiaInfo) {
          const dias = Math.abs(garantiaInfo.dias);
          if (garantiaInfo.vigente) {
            toast.success(`🛡️ Garantía vigente: quedan ${dias} día${dias === 1 ? '' : 's'} (hasta ${garantiaInfo.hasta})`, { duration: 6000 });
          } else {
            toast(`⏳ Garantía vencida hace ${dias} día${dias === 1 ? '' : 's'} (venció ${garantiaInfo.hasta})`, { duration: 6000 });
          }
        }
      } else {
        setHistorialEquipo(null);
      }
      return true;
    } catch { console.error('Error antecedentes'); return true; }
  };

  const enviarWhatsAppMantenimiento = () => {
    const cliente = db.clientes.find(c => c.id.toString() === clienteId);
    if (!cliente?.telefono) return toast.error('Sin teléfono');
    const tel = cliente.telefono.replace(/\D/g, '');
    const msg = `Hola ${cliente.nombre}, revisando el historial del dispenser S/N ${itemActual.equipoSerial}, notamos que ya le toca su mantenimiento...`;
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const sumarRepuesto = () => {
    if (estaBloqueado || !repuestoElegido) return;
    const nuevos = [...itemActual.repuestosUsados];
    const idx    = nuevos.findIndex(r => r.id === repuestoElegido.id);
    if (idx > -1) {
      nuevos[idx].cantidad += 1;
      nuevos[idx].subtotal  = nuevos[idx].cantidad * parseFloat(nuevos[idx].precio);
    } else {
      nuevos.push({
        id:                 repuestoElegido.id,
        nombre:             repuestoElegido.nombre,
        sku:                repuestoElegido.sku,
        precio:             parseFloat(repuestoElegido.precio),
        costo:              parseFloat(repuestoElegido.costo) || 0,
        porcentajeGanancia: parseFloat(repuestoElegido.porcentajeGanancia) || 0,
        cantidad:           1,
        subtotal:           parseFloat(repuestoElegido.precio)
      });
    }
    setItemActual({ ...itemActual, repuestosUsados: nuevos });
    setRepuestoElegido(null);
  };

  const actualizarCantidad = (idx, valor) => {
    const nuevos         = [...itemActual.repuestosUsados];
    const qty            = Math.max(1, parseInt(valor) || 1);
    nuevos[idx].cantidad = qty;
    nuevos[idx].subtotal = qty * parseFloat(nuevos[idx].precio);
    setItemActual({ ...itemActual, repuestosUsados: nuevos });
  };

  const quitarRepuesto = idx => {
    if (estaBloqueado) return;
    const nuevos = [...itemActual.repuestosUsados];
    nuevos.splice(idx, 1);
    setItemActual({ ...itemActual, repuestosUsados: nuevos });
  };

  const editarItem = idx => {
    if (estaBloqueado) return;
    setItemActual(ticketItems[idx]);
    setEditingIdx(idx);
    const nueva = [...ticketItems];
    nueva.splice(idx, 1);
    setTicketItems(nueva);
  };

  const eliminarItem = idx => {
    if (estaBloqueado) return;
    const nueva = [...ticketItems];
    nueva.splice(idx, 1);
    setTicketItems(nueva);
  };

  const agregarAlTicket = async (overrides) => {
    if (estaBloqueado) return;

    // Si se pasan overrides, mergear con itemActual
    const item = overrides ? { ...itemActual, ...overrides } : itemActual;

    const tieneSerial      = item.equipoSerial?.trim();
    const tieneDescripcion = item.trabajo?.trim();
    const tieneRepuestos   = item.repuestosUsados?.length > 0;
    const tieneMO          = (parseFloat(item.costoExtra) || 0) > 0;

    if (!tieneDescripcion && !tieneRepuestos && !tieneMO) {
      toast.error('Completá al menos la descripción del trabajo o agregá un repuesto');
      return;
    }

    // El equipo nuevo (N/S generado o escrito) se da de alta recién al GUARDAR el trabajo, en su
    // lugar, con modelo y ubicación (21-oct-2026). Antes se creaba acá, al agregarlo al ticket: si
    // después no se guardaba, quedaba un equipo suelto (ej. un "LS211026A" que nunca se hizo).
    // Si el equipo ya existe y el usuario cambió modelo o ubicación, actualizar en la BD
    if (!item.esNuevoEquipo && tieneSerial) {
      const equipoDB = db.equipos?.find(e => e.numeroSerie === tieneSerial);
      if (equipoDB?.id) {
        const modeloOk  = item.modeloEquipo?.trim();
        const ubicOk    = item.ubicacionEquipo?.trim();
        const diferente = (modeloOk && modeloOk !== equipoDB.modelo) ||
                          (ubicOk   && ubicOk   !== equipoDB.ubicacion);
        if (diferente) {
          api.put(`/equipos/${equipoDB.id}`, {
            numeroSerie:   equipoDB.numeroSerie,
            modelo:        modeloOk  || equipoDB.modelo    || null,
            marca:         equipoDB.marca                  || null,
            sedeId:        equipoDB.sedeId,
            ubicacion:     ubicOk    || equipoDB.ubicacion || null,
            observaciones: equipoDB.observaciones          || null,
          }).then(() => {
            setDb(prev => ({
              ...prev,
              equipos: prev.equipos.map(e =>
                e.id === equipoDB.id
                  ? { ...e, modelo: modeloOk || e.modelo, ubicacion: ubicOk || e.ubicacion }
                  : e
              ),
            }));
          }).catch(() => {});
        }
      }
    }

    const extra  = Math.max(0, parseFloat(item.costoExtra) || 0);
    const totalR = item.repuestosUsados.reduce((a, b) => a + b.subtotal, 0);
    const nuevoRenglon = {
      ...item,
      equipoSerial:   tieneSerial || 'SIN-SN',
      costoExtra:     extra,
      totalCalculado: extra + totalR,
      resumenTexto:   tieneSerial
        ? [tieneDescripcion, extra > 0 ? `MO: $${extra}` : null].filter(Boolean).join(' | ')
        : `VENTA: ${item.repuestosUsados.map(r => `${r.cantidad}x ${r.nombre}`).join(', ')}`,
      esVisita:       item.esVisita || false,
    };
    if (editingIdx !== null) {
      setTicketItems(prev => {
        const copia = [...prev];
        copia.splice(editingIdx, 0, nuevoRenglon);
        return copia;
      });
      setEditingIdx(null);
    } else {
      setTicketItems(prev => [...prev, nuevoRenglon]);
    }
    // Pre-llenar con moBase (ya incluye IVA)
    const defaultMO = Number(configGlobal?.manoDeObraBase) || 72600;
    setItemActual(prev => ({
      ...prev,
      equipoSerial: '', trabajo: '', costoExtra: defaultMO,
      repuestosUsados: [], modeloEquipo: '', ubicacionEquipo: '',
      fotoAntes: null, fotoDespues: null, fotosExtra: [], fotosRotulos: null, esNuevoEquipo: false,
    }));
    setHistorialEquipo(null);
    setRepuestoElegido(null);
    toast.success('✓ Equipo agregado al ticket');
  };

  // Sube una foto al backend y devuelve su filename, o null si no hay foto.
  // Acepta data URL (string 'data:...') — las fotos se guardan así en el estado
  // desde que FotoUpload convierte el File a data URL al seleccionarlo.
  // Usa fetch nativo (NO Axios): la instancia Axios tiene Content-Type: application/json
  // hardcodeado y en Axios 1.x no se sobreescribe aunque se pase FormData.
  const subirFoto = async (src) => {
    if (!src) return null;
    // Convertir data URL a Blob para poder subirla como multipart
    let blob;
    try {
      const r = await fetch(src);
      blob = await r.blob();
    } catch { return null; }

    const fd = new FormData();
    fd.append('file', blob, 'foto.jpg');
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${baseUrl}/uploads`, {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { console.error('Error subiendo foto, status:', res.status); return null; }
      const data = await res.json();
      return data.filename || null;
    } catch (e) {
      console.error('Error subiendo foto:', e);
      return null;
    }
  };

  const finalizar = async (confirmarTrabajo = false, overrides = {}) => {
    if (estaBloqueado || ticketItems.length === 0) return;

    const loading = toast.loading(confirmarTrabajo ? 'Confirmando...' : 'Guardando...');
    try {
      const clienteObj = db.clientes?.find(c => c.id?.toString() === clienteId);

      // ── Resolver sede ────────────────────────────────────────────────────
      // Para cliente NUEVO (sin registrar) → siempre Mostrador
      // Para cliente REGISTRADO → su sede seleccionada, o su única sede, o Mostrador
      const sedeMostrador = db.sedes?.find(s =>
        s.nombreSede?.toLowerCase().includes('mostrador') ||
        s.nombreSede?.toLowerCase().includes('particular')
      );

      let sedeIdFinal, nombreSedeF;

      if (!clienteId) {
        // Cliente nuevo rápido — dirección obligatoria, siempre crea cliente + sede
        const dir = overrides.direccionLibre;
        try {
          const dirStr = [dir.calle, dir.numero].filter(Boolean).join(' ')
            + (dir.localidad ? `, ${dir.localidad}` : '');
          await guardarUbicacionManual(dirStr, puntoLibre);
          const { data: nuevoCliente } = await api.post('/clientes', {
            clienteTipo: 'PARTICULAR',
            nombre: toTitleCase(overrides.clienteNombre || 'Particular'),
            telefono: overrides.telefono || null,
            calle: toTitleCase(dir.calle),
            numero: dir.numero || '0',
            piso: dir.piso || null,
            depto: dir.depto || null,
            localidad: toTitleCase(dir.localidad),
            provincia: 'Buenos Aires',
            direccion: dirStr,
            condicionIva: 'CONSUMIDOR_FINAL',
          });
          const { data: nuevaSede } = await api.post('/sedes', {
            clienteId: nuevoCliente.id,
            nombreSede: 'Principal',
            calle: toTitleCase(dir.calle),
            numero: dir.numero || '0',
            piso: dir.piso || null,
            depto: dir.depto || null,
            localidad: toTitleCase(dir.localidad),
            provincia: 'Buenos Aires',
            direccion: dirStr,
          });
          setDb(prev => ({
            ...prev,
            clientes: [...(prev.clientes || []), nuevoCliente],
            sedes: [...(prev.sedes || []), nuevaSede],
          }));
          sedeIdFinal = nuevaSede.id;
          nombreSedeF = nuevaSede.nombreSede;
          overrides.clienteNombre = nuevoCliente.nombre;
          // Si el guardado del trabajo falla más abajo, el reintento usa ESTE cliente
          // (antes cada intento creaba otro cliente igual: "Miriam Hospital Argerich" x3).
          setClienteId(String(nuevoCliente.id));
        } catch (err) {
          console.error('Error auto-creando cliente/sede:', err);
          toast.error('No se pudo crear el cliente — revisá los datos');
          return false;
        }
      } else {
        // Cliente registrado → sede elegida > única sede > auto-crear Principal > Mostrador
        const sedesCliente = db.sedes?.filter(s => s.cliente?.id?.toString() === clienteId) || [];
        const sedeElegida  = itemActual.sedeId || ticketItems[0]?.sedeId;

        if (sedeElegida) {
          sedeIdFinal = sedeElegida;
        } else if (sedesCliente.length === 1) {
          sedeIdFinal = sedesCliente[0].id;
        } else if (sedesCliente.length === 0) {
          // Sin sedes: crear "Principal" con la dirección del cliente automáticamente
          const sedePrincipal = await crearSedePrincipalSiNoTiene(clienteId);
          sedeIdFinal = sedePrincipal?.id || sedeMostrador?.id || db.sedes?.[0]?.id;
        } else {
          sedeIdFinal = sedeMostrador?.id || db.sedes?.[0]?.id;
        }

        nombreSedeF = db.sedes?.find(s => s.id === sedeIdFinal)?.nombreSede
          || (sedeIdFinal ? 'Principal' : 'Mostrador');
      }

      if (!sedeIdFinal) {
        toast.error('No hay ninguna sede en la base de datos. Creá una sede primero.', { id: loading });
        return false;
      }

      const tieneEquipo = ticketItems.some(it => it.equipoSerial && it.equipoSerial !== 'MOSTRADOR' && it.equipoSerial !== 'SIN-SN');
      const nombreCliente = overrides.clienteNombre || clienteObj?.nombre || 'Particular';

      // Técnico: usa el seleccionado por admin, o el usuario logueado
      const usuarioLogueado = (() => { try { return JSON.parse(localStorage.getItem('auth_usuario')); } catch { return null; } })();
      const baseLogueado = {
        id:     usuarioLogueado?.id,
        nombre: usuarioLogueado?.nombre || localStorage.getItem('tecnico_nombre') || 'Técnico',
      };
      // Garantizar que nombre nunca sea null/blank aunque el admin seleccionó un técnico sin nombre
      const tecnicoFinal = tecnicoSeleccionado?.id
        ? { id: tecnicoSeleccionado.id, nombre: tecnicoSeleccionado.nombre || baseLogueado.nombre }
        : baseLogueado;

      if (!tecnicoFinal.id) {
        toast.error('No se pudo identificar el técnico. Cerrá sesión y volvé a entrar.', { id: loading });
        return false;
      }

      // Subir fotos nuevas (data URL) al backend antes de construir el DTO.
      // Las fotos ya guardadas (filename string sin 'data:') no se vuelven a subir.
      const esNueva = s => typeof s === 'string' && s.startsWith('data:');
      let fotosConError = 0;
      const itemsConFotos = await Promise.all(ticketItems.map(async it => {
        const fotoAntesFilename   = await subirFoto(esNueva(it.fotoAntes)   ? it.fotoAntes   : null);
        const fotoDespuesFilename = await subirFoto(esNueva(it.fotoDespues) ? it.fotoDespues : null);
        if (esNueva(it.fotoAntes)   && !fotoAntesFilename)   fotosConError++;
        if (esNueva(it.fotoDespues) && !fotoDespuesFilename) fotosConError++;
        // Fotos 3 y 4 cargadas en el form (16-oct-2026)
        const fotosExtraSubidas = await Promise.all((it.fotosExtra || []).map(f => (esNueva(f) ? subirFoto(f) : f)));
        fotosExtraSubidas.forEach((f, k) => { if (esNueva((it.fotosExtra || [])[k]) && !f) fotosConError++; });
        return { ...it, fotoAntesFilename, fotoDespuesFilename, fotosExtraSubidas: fotosExtraSubidas.filter(Boolean),
          fotosRotulosSubidas: rotulosTrasSubir(it.fotosRotulos, it.fotosExtra || [], fotosExtraSubidas) };
      }));
      const subidasTrabajo = await subirFotosTrabajo(fotosTrabajo, 'trabajo');
      fotosConError += subidasTrabajo.fallaron;
      if (fotosConError > 0) {
        toast.error(`⚠ ${fotosConError} foto${fotosConError > 1 ? 's' : ''} no se ${fotosConError > 1 ? 'pudieron' : 'pudo'} subir`, { duration: 5000 });
      }

      const servicioData = {
        sedeId:             parseInt(sedeIdFinal),
        usuarioId:          tecnicoFinal.id,
        fecha:              fechaServicio,
        fechaTentativa:     fechaTentativa || false,
        ventanasDisponibles: fechaTentativa ? JSON.stringify(ventanasDisponibles || []) : null,
        servicioTipo:       tieneEquipo ? 'TECNICA' : 'VENTA',
        // Antes: si había presupuestoOrigen/ordenOrigen vinculado, forzaba REALIZADO
        // aunque confirmarTrabajo fuera false (el botón "Guardar presupuesto" terminaba
        // guardando como REALIZADO igual, contradiciendo su propia etiqueta). Se sacó esa
        // excepción: hoy es dead code en la práctica (ni `ejecutarPresupuesto` ni
        // `ejecutarOrden` en App.js se llaman desde ningún lado — MisOrdenes.jsx usa
        // EjecutarOrdenSheet, que confirma con su propio PUT sin pasar por este hook),
        // pero si algún día se vuelve a cablear, el estado ahora depende solo del botón
        // que el usuario realmente tocó — sin excepciones silenciosas.
        // Corregir un trabajo ya hecho (sin facturar) no le cambia el estado
        estado:             ['COMPLETADO', 'PENDIENTE_FACTURACION'].includes(servicioParaEditar?.estado)
          ? servicioParaEditar.estado
          : (confirmarTrabajo ? 'REALIZADO' : 'PRESUPUESTO'),
        // Bug real: si se llega acá vía una Orden despachada (Path B), `presupuestoOrigen`
        // (el parámetro) nunca se completaba aunque la orden tuviera presupuesto vinculado —
        // el servicio nuevo quedaba sin linkear al presupuesto original. `ordenOrigen.presupuestoId`
        // ya viene resuelto desde el backend (ver GET /ordenes), así que lo usamos como fallback.
        presupuestoOrigenId: presupuestoOrigen?.id || ordenOrigen?.presupuestoId || null,
        ordenId: ordenOrigen?.id || null,
        clienteNombre:      nombreCliente,
        sedeNombre:         nombreSedeF,
        descuentoPorcentaje,
        descuentoAlcance,
        observaciones: leyenda,
        fotosTrabajo: subidasTrabajo.fotos,
        duracionMinutos: duracionMinutos || null,
        aceptaTerminos: aceptaTerminos || false,
        items: itemsConFotos.map(it => {
          const esFiltro = it.trabajo?.toUpperCase().includes('FILTRO') ||
            it.repuestosUsados?.some(r => r.nombre.toUpperCase().includes('FILTRO'));
          return {
            equipoSerial:     it.equipoSerial || 'MOSTRADOR',
            tecnico:          tecnicoFinal.nombre,
            ...montosItem(it),
            metodoPago:       'EFECTIVO',
            trabajoRealizado: it.trabajo || it.resumenTexto || '',
            trabajoTipo:      it.esVisita ? 'VISITA' : (tieneEquipo ? (esFiltro ? 'CAMBIO_FILTRO' : 'REPARACION') : 'VENTA'),
            repuestosUsados:  it.repuestosUsados || [],
            garantiaHasta:    confirmarTrabajo && tieneEquipo
              ? formatDateISO(new Date(new Date().setMonth(new Date().getMonth() + 3))) // garantía: 3 meses siempre (Lucas, 29-sep-2026)
              : null,
            // Prioridad: filename de subida nueva > filename existente del backend
            // Las data URLs no se mandan al backend (ya se subieron arriba)
            fotoAntes:   it.fotoAntesFilename   || (!esNueva(it.fotoAntes)   ? it.fotoAntes   : null),
            fotoDespues: it.fotoDespuesFilename || (!esNueva(it.fotoDespues) ? it.fotoDespues : null),
            fotosExtra:  (it.fotosExtraSubidas || it.fotosExtra || []).filter(f => !esNueva(f)),
            fotosRotulos: it.fotosRotulosSubidas || it.fotosRotulos || null,
            modeloEquipo:    it.modeloEquipo || null,
            ubicacionEquipo: it.ubicacionEquipo || null,
          };
        })
      };

      let savedId;
      if (idEdicion) {
        await api.put(`/servicios/${idEdicion}`, servicioData);
        savedId = idEdicion;
      } else {
        const res = await api.post('/servicios', servicioData);
        savedId = res.data?.id;
      }

      // Si este servicio se creó a partir de un presupuesto despachado a un técnico,
      // el original queda archivado — ya cumplió su función, el registro real ahora es este.
      // Cierra el ciclo que antes dejaba el presupuesto huérfano en PRESUPUESTO para siempre.
      const idPresupuestoOrigen = presupuestoOrigen?.id || ordenOrigen?.presupuestoId || null;
      if (!idEdicion && idPresupuestoOrigen) {
        try {
          await api.patch(`/servicios/${idPresupuestoOrigen}/estado`, { estado: 'ARCHIVADO' });
        } catch {
          console.error('No se pudo archivar el presupuesto original', idPresupuestoOrigen);
        }
      }

      toast.success(confirmarTrabajo ? '✅ ¡Confirmado!' : '💾 ¡Guardado!', { id: loading });
      borrarBorrador(draftKey());

      // Capturar antes del reset para devolverlos al caller
      const resClienteId     = clienteId;
      const resClienteNombre = nombreCliente;
      const resTecnicoId     = tecnicoSeleccionado?.id || null;
      const resFechaVisita   = fechaVisita || fechaServicio || null;
      const resHoraVisita    = horaVisita || null;

      // Reset
      setTicketItems([]);
      setEditingIdx(null);
      setClienteId(null);
      setIdEdicion(null);
      setDescuentoPorcentaje(0);
      setDescuentoAlcance('TOTAL');
      setLeyenda(LEYENDA_DEFAULT);
      setFotosTrabajo([]);
      setFechaServicio(getTodayISO());
      setFechaVisita('');
      setHoraVisita('');
      setItemActual({ sedeId: '', sedeNombre: '', equipoSerial: '', trabajo: '', costoExtra: '', repuestosUsados: [] });
      return { ok: true, id: savedId, clienteId: resClienteId, clienteNombre: resClienteNombre, tecnicoId: resTecnicoId, fechaVisita: resFechaVisita, horaVisita: resHoraVisita };

    } catch (err) {
      console.error('Error al guardar servicio:', err.response?.data || err.message);
      const detalle = err.response?.data;
      const camposFallidos = detalle?.detalles
        ? Object.keys(detalle.detalles).filter(k => k !== 'path').join(', ')
        : null;
      const msg = camposFallidos
        ? `Campos inválidos: ${camposFallidos}`
        : (detalle?.mensaje || err.message || 'Error de servidor');
      toast.error(`❌ ${msg}`, { id: loading });
      return false;
    }
  };

  const refrescarDatos = async () => {
    try {
      const [c, s, e, r] = await Promise.all([
        api.get('/clientes?page=0&size=500'),
        api.get('/sedes?page=0&size=500'),
        api.get('/equipos?page=0&size=500'),
        api.get('/repuestos?page=0&size=500')
      ]);
      setDb({
        clientes:  c.data.content || c.data,
        sedes:     s.data.content || s.data,
        equipos:   e.data.content || e.data,
        repuestos: r.data.content || r.data
      });
    } catch { console.error('Error refrescando datos'); }
  };

  return {
    db, setDb,
    clienteId, setClienteId,
    esPresupuesto, setEsPresupuesto,
    ticketItems, setTicketItems,
    idEdicion, setIdEdicion,
    estaBloqueado, setEstaBloqueado,
    historialEquipo, setHistorialEquipo,
    itemActual, setItemActual,
    repuestoElegido, setRepuestoElegido,
    tecnicoSeleccionado, setTecnicoSeleccionado,
    fechaVisita, setFechaVisita,
    horaVisita, setHoraVisita,
    descuentoPorcentaje, setDescuentoPorcentaje,
    descuentoAlcance, setDescuentoAlcance,
    totales, obtenerTotales,
    modalClienteAbierto, setModalClienteAbierto,
    nombreClientePrellenado, setNombreClientePrellenado,
    modalSedeAbierto, setModalSedeAbierto,
    nombreSedePrellenado, setNombreSedePrellenado,
    consultarAntecedentes,
    enviarWhatsAppMantenimiento,
    sumarRepuesto,
    actualizarCantidad,
    quitarRepuesto,
    editarItem,
    eliminarItem,
    agregarAlTicket,
    finalizar,
    refrescarDatos,
    onClienteSeleccionado,
    calcularGananciaRepuesto,
    calcularResumenGanancia,
    leyenda, setLeyenda,
    fotosTrabajo, setFotosTrabajo,
    fechaServicio, setFechaServicio,
    duracionMinutos, setDuracionMinutos,
    fechaTentativa, setFechaTentativa,
    ventanasDisponibles, setVentanasDisponibles,
    aceptaTerminos, setAceptaTerminos,
    borradorDisponible,
    recuperarBorrador,
    descartarBorrador,
    configGlobal,
    modoCliente, setModoCliente,
    nombreLibre, setNombreLibre,
    telefonoLibre, setTelefonoLibre,
    dirLibre, setDirLibre, puntoLibre, setPuntoLibre,
  };
}